/**
 * Reserved enum filter value when the user selects the optional “empty / unset” row in AtomTable.
 * Serialized to `{ field: { isNull: true } }` in find parameters. Do not use as a real enum value.
 */
export const ATOM_TABLE_ENUM_FILTER_EMPTY = "__atomTableEnumEmpty__" as const;

export function isAtomTableEnumFilterEmptyValue(
  value: string | undefined,
): value is typeof ATOM_TABLE_ENUM_FILTER_EMPTY {
  return value === ATOM_TABLE_ENUM_FILTER_EMPTY;
}

/**
 * The `enableEnumFiltering` option of a table column: the enum filter in the
 * column header. The same for every atom table of the antd and shadcn modules.
 */
export interface AtomTableEnumFilterConfig {
  /** The values the filter offers. */
  enumOptions: string[];
  /**
   * Single-select: a search field over the options (in antd a free-text input
   * with suggestions). With `multiple`: a searchable list of the options.
   */
  inputField?: boolean;
  /** Maps a stored enum value to its label (options and active-filter tags). */
  enumOptionLabels?: Record<string, string>;
  /**
   * When set, adds an option that matches rows where the field is null or
   * unset (`isNull` in the find parameters).
   */
  enumEmptyOptionLabel?: string;
  /**
   * Lets the user select several values; a row matches when it has one of
   * them. Default `false`: one value.
   */
  multiple?: boolean;
}

/**
 * State of one enum column filter: a string for a single-select column, a list
 * for a `multiple` column. Read it through {@link toAtomTableEnumFilterValues}.
 */
export type AtomTableEnumFilterValue = string | readonly string[];

/** Enum filter state of a table, by column id. */
export type AtomTableEnumFilterMap = Record<
  string,
  AtomTableEnumFilterValue | undefined
>;

/**
 * The selected values of an enum column filter as a list, whatever shape the
 * state has: nothing, a string (single-select, or persisted before the column
 * became `multiple`) or a list. Empty and repeated members are dropped.
 */
export function toAtomTableEnumFilterValues(value: unknown): string[] {
  const members: unknown[] = Array.isArray(value) ? value : [value];
  const values = members.filter(
    (member): member is string => typeof member === "string" && member !== "",
  );
  return [...new Set(values)];
}

/**
 * The values in the order of the options, whatever the order they were
 * selected or stored in, so both table variants send the same find clause
 * and show the tags alike. Values that are not among the options keep their
 * relative order at the end. Every value comes once, also when the options
 * or the values list it twice.
 */
export function sortByOptionOrder(
  values: readonly string[],
  options: ReadonlyArray<{ readonly value: string }>,
): string[] {
  const selected = new Set(values);
  // A Set: an option or a value listed twice still gives one entry
  const offered = new Set(options.map((option) => option.value));
  return [
    ...[...offered].filter((value) => selected.has(value)),
    ...[...selected].filter((value) => !offered.has(value)),
  ];
}
